import { Avatar, Button } from '@cambridgeassessment/cambridge-ui';
import { Box, Link, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { ORG_USER_STATUS } from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import useFeatureFlags from 'src/hooks/useFeatureFlags';
import useOrgUserDetails from 'src/hooks/useOrgUserDetails';
import { OrgUserStatus as OrgUserStatusType } from 'src/types';
import { toDateString } from 'src/utils/transformers';
import OrgUserActions from '../OrgUserActions/OrgUserActions';
import OrgUserDetailsModal from '../OrgUserDetailsModal/OrgUserDetailsModal';
import OrgUserApplicationRoles from '../OrgUserRoles/OrgUserApplicationRoles';
import OrgUserStatus from '../OrgUserStatus/OrgUserStatus';
import { useStyles } from './styles';

export type OrgUserDetailsProps = {
  disableActions?: boolean;
};

const OrgUserDetails: React.FC<OrgUserDetailsProps> = ({ disableActions }) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState<
    'creditorNumber' | 'businessUnit' | 'examinerId' | null
  >(null);
  const {
    user: { isCambridgeAssessor },
  } = useAuth();
  const { userDetails, resendInvitationLink } = useOrgUserDetails();
  const isExaminer = userDetails?.roles?.find((role) => role === 'EXAMINER');
  const { flags } = useFeatureFlags();
  const showResendInviteLink = flags['mycambridge.resend-invitation'];
  return (
    <Box>
      <Box mt={3} display="flex" alignItems="center">
        <Box mr={3}>
          <Avatar
            src={userDetails?.profilePicture}
            style={{ height: 75, width: 75, lineHeight: 38, fontSize: 32 }}
          />
        </Box>
        <Box>
          <Typography
            variant="h2"
            data-testid="user-full-name"
            className={classes.breakWordd}
          >
            {userDetails.firstName} {userDetails.lastName}
          </Typography>
          <Box
            mt={1}
            display="flex"
            flexDirection="row"
            flexWrap={['wrap', 'nowrap']}
          >
            <Box mr={3}>
              <OrgUserStatus status={userDetails.status} />
            </Box>
            {userDetails.lastLoginTimestamp > 0 && (
              <Box data-testid="user-last-active" display="flex">
                <Typography variant="subtitle2">Last active:</Typography>
                &nbsp;
                <Typography
                  variant="body1"
                  style={{ fontSize: '1.15em', lineHeight: '1.2em' }}
                >
                  {toDateString(userDetails.lastLoginTimestamp)}
                </Typography>
              </Box>
            )}
          </Box>
          {userDetails.status.toUpperCase() !==
            ORG_USER_STATUS.ACTIVE.toUpperCase() && (
            <Box
              mt={1}
              display="flex"
              flexDirection="row"
              flexWrap={['wrap', 'nowrap']}
            >
              <Box data-testid="user-invited-date" display="flex">
                <Typography variant="subtitle2">Invited date:</Typography>
                &nbsp;
                <Typography variant="body1">
                  {userDetails.invitedDate
                    ? toDateString(userDetails.invitedDate)
                    : 'Not available'}
                </Typography>
              </Box>
            </Box>
          )}
          {showResendInviteLink &&
            userDetails.status.toUpperCase() !==
              ORG_USER_STATUS.ACTIVE.toUpperCase() && (
              <Box
                mt={1}
                display="flex"
                flexDirection="row"
                flexWrap={['wrap', 'nowrap']}
              >
                <Link
                  data-testid="resend-invitation-link"
                  onClick={() => resendInvitationLink(userDetails)}
                >
                  Resend invitation
                </Link>
              </Box>
            )}
        </Box>
      </Box>
      <Box mt={4}>
        <Box mb={4}>
          <Typography variant="h4" data-testid="user-details-title">
            Details
          </Typography>
        </Box>
        <Box mb={3} data-testid="user-firstname-section">
          <Box mb={1}>
            <Typography variant="subtitle2">
              First name or given name
            </Typography>
          </Box>
          <Typography variant="body1" className={classes.breakWord}>
            {userDetails.firstName}
          </Typography>
        </Box>
        <Box mb={3} data-testid="user-lastname-section">
          <Box mb={1}>
            <Typography variant="subtitle2">Surname or family name</Typography>
          </Box>
          <Typography variant="body1" className={classes.breakWord}>
            {userDetails.lastName}
          </Typography>
        </Box>
        <Box mb={3} data-testid="user-email-section">
          <Box mb={1}>
            <Typography variant="subtitle2">Email address</Typography>
          </Box>
          <Typography variant="body1" className={classes.breakWord}>
            {userDetails.email}
          </Typography>
        </Box>
        {isExaminer && (
          <Box mb={3} data-testid="user-examinerId-section">
            <Box mb={1} display="flex">
              <Typography variant="subtitle2" className={classes.breakWord}>
                Examiner ID
              </Typography>
              <Button
                size="small"
                variant="text"
                data-testid="changeExaminerId-button"
                className={classes.editButton}
                onClick={() => setModalOpen('examinerId')}
              >
                {userDetails.examinerId ? 'Edit' : 'Add'}
              </Button>
            </Box>
            <Typography variant="body1" className={classes.editableText}>
              {userDetails.examinerId && userDetails.examinerId.length
                ? userDetails.examinerId
                : '-'}
            </Typography>
          </Box>
        )}
        {isCambridgeAssessor && (
          <>
            <Box mb={3} data-testid="user-creditorNumber-section">
              <Box mb={1} display="flex">
                <Typography variant="subtitle2" className={classes.breakWord}>
                  Creditor number
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  data-testid="changeCreditorNumber-button"
                  className={classes.editButton}
                  onClick={() => setModalOpen('creditorNumber')}
                >
                  {userDetails.creditorNumber ? 'Edit' : 'Add'}
                </Button>
              </Box>
              <Typography variant="body1" className={classes.editableText}>
                {userDetails.creditorNumber ?? '-'}
              </Typography>
            </Box>
            <Box mb={3} data-testid="user-businessUnit-section">
              <Box mb={1} display="flex">
                <Typography variant="subtitle2" className={classes.breakWord}>
                  Business unit
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  data-testid="changeBusinessUnit-button"
                  className={classes.editButton}
                  onClick={() => setModalOpen('businessUnit')}
                >
                  {userDetails.businessStream ? 'Edit' : 'Add'}
                </Button>
              </Box>
              <Typography variant="body1" className={classes.editableText}>
                {userDetails.businessStream?.join(', ') ?? '-'}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <Box mt={3}>{!disableActions && <OrgUserActions />}</Box>
      {userDetails.status !== OrgUserStatusType.Active ? (
        <Box>
          <Box mt={5}>
            <Typography variant="h4" data-testid="user-roles-title">
              Roles
            </Typography>
          </Box>
          <Box mt={2} data-testid="user-noroles-section">
            <Typography variant="body1">
              Sorry, roles cannot be displayed for inactive and/or users that
              have not yet verified their accounts.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box mt={4}>
          <OrgUserApplicationRoles disabled={disableActions} />
        </Box>
      )}
      <OrgUserDetailsModal
        open={!!modalOpen}
        onClose={() => setModalOpen(null)}
        variant={modalOpen}
      />
    </Box>
  );
};

export default hot(OrgUserDetails);
